import { useState, useMemo } from "react";
import {
  ChevronLeft,
  ChevronRight,
  Calendar,
  Users,
  Filter,
  Eye,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

export default function LeaveCalendar({ leaveRequests, leaveTypes }) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDepartment, setSelectedDepartment] = useState("all");
  const [selectedLeaveType, setSelectedLeaveType] = useState("all");
  const [viewMode, setViewMode] = useState("month"); // month, week

  // Get current month data
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);

  // Create calendar grid
  const startDate = new Date(firstDay);
  startDate.setDate(startDate.getDate() - firstDay.getDay());

  const endDate = new Date(lastDay);
  endDate.setDate(endDate.getDate() + (6 - lastDay.getDay()));

  const calendarDays = [];
  const current = new Date(startDate);

  while (current <= endDate) {
    calendarDays.push(new Date(current));
    current.setDate(current.getDate() + 1);
  }

  // Helper function to parse dates without timezone issues
  const parseDate = (dateString) => {
    // Extract just the date part (YYYY-MM-DD) to avoid timezone conversion
    const dateOnly = dateString.split("T")[0];
    const [year, month, day] = dateOnly.split("-").map(Number);
    return new Date(year, month - 1, day); // month is 0-indexed in Date constructor
  };

  // Filter leave requests for current view
  const filteredLeaves = useMemo(() => {
    return leaveRequests.filter((leave) => {
      const leaveStart = parseDate(leave.date_debut);
      const leaveEnd = parseDate(leave.date_fin);

      // Check if leave overlaps with current month
      const monthOverlap = leaveStart <= lastDay && leaveEnd >= firstDay;

      // Apply filters
      const departmentMatch =
        selectedDepartment === "all" ||
        leave.employe.departement?.nom === selectedDepartment;

      const typeMatch =
        selectedLeaveType === "all" ||
        leave.type_conge.id === selectedLeaveType;

      const statusMatch = leave.statut === "approuve";

      return monthOverlap && departmentMatch && typeMatch && statusMatch;
    });
  }, [
    leaveRequests,
    year,
    month,
    selectedDepartment,
    selectedLeaveType,
    firstDay,
    lastDay,
  ]);

  // Get leaves for a specific day
  const getLeavesForDay = (date) => {
    return filteredLeaves.filter((leave) => {
      const leaveStart = parseDate(leave.date_debut);
      const leaveEnd = parseDate(leave.date_fin);
      return date >= leaveStart && date <= leaveEnd;
    });
  };

  // Navigation functions
  const goToPreviousMonth = () => {
    setCurrentDate(new Date(year, month - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentDate(new Date(year, month + 1, 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Get unique departments
  const departments = [
    ...new Set(
      leaveRequests.map((req) => req.employe.departement?.nom).filter(Boolean)
    ),
  ];

  const monthNames = [
    "Janvier",
    "Février",
    "Mars",
    "Avril",
    "Mai",
    "Juin",
    "Juillet",
    "Août",
    "Septembre",
    "Octobre",
    "Novembre",
    "Décembre",
  ];

  const dayNames = ["Dim", "Lun", "Mar", "Mer", "Jeu", "Ven", "Sam"];

  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const isCurrentMonth = (date) => {
    return date.getMonth() === month;
  };

  return (
    <div className="space-y-6">
      {/* Calendar Header */}
      <Card>
        <CardHeader>
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={goToPreviousMonth}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <h2 className="text-xl font-semibold min-w-[200px] text-center">
                  {monthNames[month]} {year}
                </h2>
                <Button variant="outline" size="sm" onClick={goToNextMonth}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
              <Button variant="outline" size="sm" onClick={goToToday}>
                Aujourd'hui
              </Button>
            </div>

            {/* Filters */}
            <div className="flex items-center gap-3">
              <Select
                value={selectedDepartment}
                onValueChange={setSelectedDepartment}
              >
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Département" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les départements</SelectItem>
                  {departments.map((dept) => (
                    <SelectItem key={dept} value={dept}>
                      {dept}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select
                value={selectedLeaveType}
                onValueChange={setSelectedLeaveType}
              >
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Type de congé" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les types</SelectItem>
                  {leaveTypes.map((type) => (
                    <SelectItem key={type.id} value={type.id}>
                      <div className="flex items-center gap-2">
                        <div
                          className="w-3 h-3 rounded"
                          style={{ backgroundColor: type.couleur }}
                        ></div>
                        {type.nom}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Calendar Grid */}
      <Card>
        <CardContent className="p-0">
          {/* Day Headers */}
          <div className="grid grid-cols-7 border-b">
            {dayNames.map((day) => (
              <div
                key={day}
                className="p-4 text-center font-medium text-sm text-muted-foreground bg-gray-50"
              >
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Days */}
          <div className="grid grid-cols-7">
            {calendarDays.map((date, index) => {
              const dayLeaves = getLeavesForDay(date);
              const isCurrentMonthDay = isCurrentMonth(date);
              const isTodayDate = isToday(date);

              return (
                <div
                  key={index}
                  className={cn(
                    "min-h-[120px] p-2 border-r border-b",
                    !isCurrentMonthDay && "bg-gray-50 text-muted-foreground",
                    isTodayDate && "bg-blue-50"
                  )}
                >
                  <div
                    className={cn(
                      "flex items-center justify-center w-8 h-8 text-sm font-medium rounded-full mb-2",
                      isTodayDate && "bg-blue-600 text-white",
                      !isTodayDate && isCurrentMonthDay && "hover:bg-gray-100",
                      !isCurrentMonthDay && "text-muted-foreground"
                    )}
                  >
                    {date.getDate()}
                  </div>

                  {/* Leave indicators */}
                  <div className="space-y-1">
                    {dayLeaves.slice(0, 3).map((leave, leaveIndex) => (
                      <div
                        key={`${leave.id}-${leaveIndex}`}
                        className="text-xs p-1 rounded text-white truncate cursor-pointer hover:opacity-80"
                        style={{ backgroundColor: leave.type_conge.couleur }}
                        title={`${leave.employe.prenom} ${leave.employe.nom} - ${leave.type_conge.nom}`}
                      >
                        {leave.employe.prenom} {leave.employe.nom.charAt(0)}.
                      </div>
                    ))}

                    {dayLeaves.length > 3 && (
                      <div className="text-xs text-muted-foreground text-center">
                        +{dayLeaves.length - 3} autre(s)
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Legend and Summary */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Legend */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5" />
              Légende
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {leaveTypes.map((type) => (
                <div key={type.id} className="flex items-center gap-3">
                  <div
                    className="w-4 h-4 rounded"
                    style={{ backgroundColor: type.couleur }}
                  ></div>
                  <span className="text-sm">{type.nom}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Monthly Summary */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Résumé du mois
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">
                  Total des congés:
                </span>
                <Badge variant="outline">
                  {filteredLeaves.length} demande(s)
                </Badge>
              </div>

              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">
                  Jours d'absence:
                </span>
                <Badge variant="outline">
                  {filteredLeaves.reduce(
                    (sum, leave) => sum + leave.nombre_jours,
                    0
                  )}{" "}
                  jour(s)
                </Badge>
              </div>

              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">
                  Employés concernés:
                </span>
                <Badge variant="outline">
                  {
                    new Set(filteredLeaves.map((leave) => leave.employe.id))
                      .size
                  }{" "}
                  employé(s)
                </Badge>
              </div>

              {/* Department breakdown */}
              <div className="pt-4 border-t">
                <h4 className="text-sm font-medium mb-2">Par département:</h4>
                <div className="space-y-2">
                  {departments.map((dept) => {
                    const deptLeaves = filteredLeaves.filter(
                      (leave) => leave.employe.departement?.nom === dept
                    );
                    return (
                      <div key={dept} className="flex justify-between text-sm">
                        <span>{dept}</span>
                        <span>{deptLeaves.length}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
